import { useState } from 'react';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [showLegalModal, setShowLegalModal] = useState(false);

  return (
    <>
      <footer className="bg-emerald-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-6">
            {/* Company Info */}
            <div className="md:col-span-2">
              <img 
                src="https://static.readdy.ai/image/a48e56127586ecde9cf388aadcd5ef1b/19c26b1626dd19f7cd936f84555985c9.jpeg" 
                alt="GreenState Investments" 
                className="h-12 w-auto mb-4 bg-white rounded px-2 py-1"
              />
              <p className="text-emerald-100 mb-4 leading-relaxed">
                GreenState Investments is a nationwide real estate investment firm dedicated to helping homeowners sell their properties quickly and easily. We buy houses in any condition, anywhere in the United States.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#about" className="text-emerald-100 hover:text-white transition-colors text-sm cursor-pointer">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" className="text-emerald-100 hover:text-white transition-colors text-sm cursor-pointer">
                    How It Works
                  </a>
                </li>
                <li>
                  <a href="#videos" className="text-emerald-100 hover:text-white transition-colors text-sm cursor-pointer">
                    Success Stories
                  </a>
                </li>
                <li>
                  <a href="#seller-form" className="text-emerald-100 hover:text-white transition-colors text-sm cursor-pointer">
                    Get Cash Offer
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-phone-line text-emerald-300 mt-1"></i>
                  <div>
                    <p className="text-sm text-emerald-100">Call Us</p>
                    <a href="tel:202-600-9590" className="text-white font-medium hover:text-emerald-200 transition-colors cursor-pointer">
                      202-600-9590
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line text-emerald-300 mt-1"></i>
                  <div>
                    <p className="text-sm text-emerald-100">Email Us</p>
                    <a href="mailto:info@greenstateinvestments.com" className="text-white font-medium hover:text-emerald-200 transition-colors cursor-pointer">
                      info@greenstateinvestments.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-emerald-300 mt-1"></i>
                  <div>
                    <p className="text-sm text-emerald-100">Nationwide Service</p>
                    <p className="text-white font-medium">All 50 States</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-emerald-600 pt-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-emerald-100 text-sm">
                © 1998 - 2026 GreenState Investments. All rights reserved.
              </p>
              <div className="flex gap-6">
                <button 
                  onClick={() => setShowLegalModal(true)}
                  className="text-emerald-100 hover:text-white text-sm transition-colors cursor-pointer"
                >
                  Legal Terms & Privacy Policy
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Legal Terms & Privacy Policy</h2>
              <button 
                onClick={() => setShowLegalModal(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="px-6 py-6 text-gray-700 leading-relaxed">
              <p className="text-sm text-gray-600 mb-6">Last Updated: 3/10/2026</p>
              
              <h3 className="text-lg font-bold text-gray-900 mb-3">1. Nature of Services (Real Estate Disclosure)</h3>
              <p className="mb-6">
                GreenState Investments, LLC ("we," "us," or "our") is a real estate investment firm. We are not licensed real estate brokers or agents. We act as a principal buyer or assignor in real estate transactions. Any information provided on this website is for informational purposes only and does not constitute legal, financial, or real estate advice.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3">2. Information We Collect & How We Use It</h3>
              <p className="mb-3">
                We only collect information that you voluntarily provide through our website forms, such as your name, email address, phone number, and property details.
              </p>
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li><strong>Purpose:</strong> This data is used exclusively to evaluate property for potential purchase and to communicate with you regarding your submission.</li>
                <li><strong>No Sale of Data:</strong> We do not sell, rent, or trade your personal information to third-party marketers.</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3">3. Third-Party Service Providers</h3>
              <p className="mb-6">
                To operate our website and business, your data may pass through or be stored by trusted third-party service providers (e.g., website hosting, security services, or CRM tools). These parties are contractually obligated to keep your information confidential and use it only to provide services to us.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3">4. Cookies and Tracking</h3>
              <p className="mb-6">
                We do not use tracking or advertising cookies. However, our website may use "functional cookies" required for basic site operation, security (via our hosting providers), or to display embedded content (such as YouTube videos). By using this site, you consent to these necessary functional elements.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3">5. TCPA & Communication Consent</h3>
              <p className="mb-6">
                By submitting your information, you provide express written consent for GreenState Investments, LLC to contact you via phone, text, or email regarding your property, even if your number is on a State or Federal "Do Not Call" list. You may opt out of these communications at any time by replying "STOP" to any text or clicking "Unsubscribe" in emails.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3">6. Limitation of Liability</h3>
              <p className="mb-6">
                To the maximum extent permitted by law, GreenState Investments, LLC shall not be liable for any damages resulting from your use of this website or any information contained herein. All property evaluations are "as-is" and do not constitute a binding offer to purchase until a formal contract is signed by all parties.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3">7. Governing Law & Severability</h3>
              <p className="mb-6">
                These terms are governed by the laws of the Commonwealth of Virginia. If any part of this policy is found to be unenforceable or invalid by a court of law, the remaining sections shall continue to be in full force and effect.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3">8. Contact Us</h3>
              <p className="mb-6">
                If you have questions regarding this policy, please contact us at: <a href="mailto:info@greenstateinvestments.com" className="text-emerald-600 hover:text-emerald-700 font-medium">info@greenstateinvestments.com</a>.
              </p>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button 
                onClick={() => setShowLegalModal(false)}
                className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}